"use client";

import { Button } from "@/components/ui/button";
import type { Editor } from "@tiptap/react";
import { Check, Play } from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { t } from "@/lib/localization";

type Props = {
    editor: Editor | null;
    rootRef: React.RefObject<HTMLElement>;
    surfaceRef: React.RefObject<HTMLElement>;
    onMarkCorrect: (blockIdx: number, wordIdx: number) => void;
};

type PopoverInfo = {
    confidence: number | null;
    bucket: string;
    blockIdx: number | null;
    wordIdx: number | null;
    blockStart: number | null;
    top: number;
    centerX: number;
};

function seekVideo(seconds: number) {
    const iframe = document.getElementById(
        "myVideo"
    ) as HTMLIFrameElement | null;
    if (!iframe?.contentWindow) return;
    iframe.contentWindow.postMessage({ type: "SEEK", payload: seconds }, "*");
}

export default function WordPopover({
    editor,
    rootRef,
    surfaceRef,
    onMarkCorrect,
}: Props) {
    const [info, setInfo] = useState<PopoverInfo | null>(null);
    const popoverRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const surface = surfaceRef.current;
        const root = rootRef.current;
        if (!surface || !root) return;

        const onClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement | null;
            if (!target) return;

            if (popoverRef.current?.contains(target)) return;

            const blockEl = target.closest<HTMLElement>(
                ".caption-block[data-start]"
            );
            if (!blockEl || !surface.contains(blockEl)) {
                setInfo(null);
                return;
            }

            const wordEl = target.closest<HTMLElement>("span[data-word]");
            if (!wordEl) {
                setInfo(null);
                return;
            }

            const blockStartAttr = blockEl.getAttribute("data-start");
            const blockStart =
                blockStartAttr != null ? parseFloat(blockStartAttr) : null;
            const blockIdxAttr = wordEl.getAttribute("data-block-idx");
            const wordIdxAttr = wordEl.getAttribute("data-word-idx");
            const confAttr = wordEl.getAttribute("data-confidence");
            const bucket = wordEl.getAttribute("data-bucket") ?? "none";

            const anchorRect = wordEl.getBoundingClientRect();
            const rootRect = root.getBoundingClientRect();
            setInfo({
                confidence: confAttr != null ? parseFloat(confAttr) : null,
                bucket,
                blockIdx:
                    blockIdxAttr != null ? parseInt(blockIdxAttr, 10) : null,
                wordIdx: wordIdxAttr != null ? parseInt(wordIdxAttr, 10) : null,
                blockStart,
                top: anchorRect.bottom - rootRect.top + 6,
                centerX:
                    (anchorRect.left + anchorRect.right) / 2 - rootRect.left,
            });
        };

        document.addEventListener("click", onClick);
        return () => {
            document.removeEventListener("click", onClick);
        };
    }, [rootRef, surfaceRef]);

    useLayoutEffect(() => {
        if (!info || !popoverRef.current || !rootRef.current) return;
        const bubble = popoverRef.current;
        const rootW = rootRef.current.clientWidth;
        const bubbleW = bubble.offsetWidth;
        let left = info.centerX - bubbleW / 2;
        left = Math.max(8, Math.min(rootW - bubbleW - 8, left));
        bubble.style.left = `${left}px`;
        bubble.style.top = `${info.top}px`;
    }, [info, rootRef]);

    useEffect(() => {
        if (!info) return;
        const surface = surfaceRef.current;
        if (!surface) return;
        const close = () => setInfo(null);
        surface.addEventListener("scroll", close);
        window.addEventListener("resize", close);
        return () => {
            surface.removeEventListener("scroll", close);
            window.removeEventListener("resize", close);
        };
    }, [info, surfaceRef]);

    if (!info) return null;

    const isFlagged = info.bucket === "red" || info.bucket === "amber";
    const canMarkCorrect =
        isFlagged && info.blockIdx != null && info.wordIdx != null;
    const canSeek = info.blockStart != null;

    const handleMarkCorrect = () => {
        if (!editor || info.blockIdx == null || info.wordIdx == null) return;

        onMarkCorrect(info.blockIdx, info.wordIdx);

        const wordType = editor.schema.marks.word;
        const tr = editor.state.tr;
        editor.state.doc.descendants((node, pos) => {
            if (!node.isText) return;
            const mark = node.marks.find((m) => m.type === wordType);
            if (!mark) return;
            if (
                mark.attrs.blockIdx !== info.blockIdx ||
                mark.attrs.wordIdx !== info.wordIdx
            )
                return;
            const from = pos;
            const to = pos + node.nodeSize;
            tr.removeMark(from, to, wordType);
            tr.addMark(
                from,
                to,
                wordType.create({
                    ...mark.attrs,
                    bucket: "none",
                    confidence: 1,
                })
            );
        });
        editor.view.dispatch(tr);
        setInfo(null);
    };

    const handleSeek = () => {
        if (info.blockStart == null) return;
        seekVideo(info.blockStart);
    };

    const confPct =
        info.confidence != null ? Math.round(info.confidence * 100) : null;

    return (
        <div
            ref={popoverRef}
            className="absolute z-50 flex items-center gap-2 rounded-lg border border-border bg-popover p-2 shadow-lg"
            style={{ top: info.top, left: 0 }}
            onMouseDown={(e) => e.preventDefault()}
        >
            {confPct != null && (
                <span className="px-1 text-xs text-muted-foreground">
                    {t(
                        "video-sheet.captions-tab.editor.word-popover.confidence",
                        undefined,
                        { percent: confPct }
                    )}
                </span>
            )}
            {canSeek && (
                <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    className="h-7 gap-1 px-2 text-xs"
                    onClick={handleSeek}
                >
                    <Play className="size-3.5" />
                    {t("video-sheet.captions-tab.editor.word-popover.seek")}
                </Button>
            )}
            {canMarkCorrect && (
                <Button
                    type="button"
                    size="sm"
                    className="h-7 gap-1 px-2 text-xs"
                    onClick={handleMarkCorrect}
                >
                    <Check className="size-3.5" />
                    {t(
                        "video-sheet.captions-tab.editor.word-popover.mark-correct"
                    )}
                </Button>
            )}
        </div>
    );
}
